import { useSelectedOrg, useSettlementCreditDetails } from "@fero/hooks";
import type { UseQueryResult } from "@tanstack/react-query";
import type { BigDecimal } from "adl-gen/common";
import type { TopLevelUnitType } from "adl-gen/ferovinum/app/db";
import type { SettlementCreditDetailsResp } from "adl-gen/ferovinum/app/settlementcredit";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createBigDecimalCurrencyFormatter } from "utils/currency-utils";
import { ensure } from "utils/null-and-undefined-utils";
import type { StockDataDisplayInfo } from "./stock-listing-table";

interface Input {
  stockDataLookup: Partial<Record<TopLevelUnitType, StockDataDisplayInfo>>;
  loadMoreStockDataMap: (
    topLevelUnitType: TopLevelUnitType,
    offset: number,
  ) => Promise<StockDataDisplayInfo["stockDataMap"] | undefined>;
}

type OrganisationDashboardPageViewViewModel = {
  availableStockTypes: Set<TopLevelUnitType>;
  stockDataByTopLevelUnitType: Partial<
    Record<TopLevelUnitType, StockDataDisplayInfo>
  >;
  initLoadingListings: boolean;
  topLevelUnitType?: TopLevelUnitType;
  organisationCredit: UseQueryResult<SettlementCreditDetailsResp, unknown>;
  currencyFormatter: { format: (value: BigDecimal) => string };
  loadMore: (
    offset: number,
  ) => Promise<StockDataDisplayInfo["stockDataMap"] | undefined>;
  stockDataDisplayInfo?: StockDataDisplayInfo;
  setTopLevelUnitType: Dispatch<SetStateAction<TopLevelUnitType | undefined>>;
};

export const useOrganisationDashboardPageViewViewModel = ({
  stockDataLookup,
  loadMoreStockDataMap,
}: Input): OrganisationDashboardPageViewViewModel => {
  const availableStockTypes = useMemo<Set<TopLevelUnitType>>(
    () => new Set(Object.keys(stockDataLookup) as TopLevelUnitType[]),
    [stockDataLookup],
  );
  const [stockDataByTopLevelUnitType, setStockDataByTopLevelUnitType] =
    useState(stockDataLookup);
  const [initLoadingListings, setInitLoadingListings] = useState(false);
  const [topLevelUnitType, setTopLevelUnitType] = useState<
    TopLevelUnitType | undefined
  >(
    availableStockTypes.size > 0
      ? [...availableStockTypes].sort()[0]
      : undefined,
  );
  const organisation = ensure(useSelectedOrg());
  const organisationCredit = useSettlementCreditDetails({
    currency: organisation.value.currency,
    counterpartyRef: {
      kind: "organisation",
      value: organisation.id,
    },
  });
  const currencyFormatter = createBigDecimalCurrencyFormatter(
    organisation.value.currency,
  )();

  useEffect(() => {
    const firstAvailableStockType = [...availableStockTypes].sort()[0];
    setTopLevelUnitType(firstAvailableStockType);
  }, [availableStockTypes]);

  useEffect(() => {
    setStockDataByTopLevelUnitType(stockDataLookup);
  }, [stockDataLookup]);

  const stockDataDisplayInfo = topLevelUnitType
    ? stockDataByTopLevelUnitType[topLevelUnitType]
    : undefined;

  useEffect(() => {
    if (!topLevelUnitType) return;
    if (stockDataByTopLevelUnitType[topLevelUnitType]?.stockDataMap?.size)
      return;
    setInitLoadingListings(true);
    loadMoreStockDataMap(topLevelUnitType, 0).then((stockDataMap) => {
      setInitLoadingListings(false);
      setStockDataByTopLevelUnitType((previous) => ({
        ...previous,
        [topLevelUnitType]: {
          ...previous[topLevelUnitType],
          stockDataMap,
        },
      }));
    });
  }, [topLevelUnitType, stockDataByTopLevelUnitType, loadMoreStockDataMap]);

  const loadMore = useCallback(
    async (offset: number) => {
      return (
        topLevelUnitType &&
        (await loadMoreStockDataMap(topLevelUnitType, offset))
      );
    },
    [loadMoreStockDataMap, topLevelUnitType],
  );

  return {
    availableStockTypes,
    stockDataByTopLevelUnitType,
    initLoadingListings,
    topLevelUnitType,
    organisationCredit,
    currencyFormatter,
    stockDataDisplayInfo,
    loadMore,
    setTopLevelUnitType,
  };
};
